import { useEffect, useState } from 'react';
import type { ResourceName, ResourceResponse } from '@jarvis/shared';
import { fetchResource } from './api';
import { usePrefs } from './prefs';

export type ResourceState<K extends ResourceName> =
  | { status: 'loading' }
  | { status: 'ready'; response: ResourceResponse<K> }
  | { status: 'error'; error: Error & { code?: string } };

export function useResource<K extends ResourceName>(name: K) {
  const { stateMode } = usePrefs();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<ResourceState<K>>({ status: 'loading' });

  useEffect(() => {
    const ac = new AbortController();
    setState({ status: 'loading' });
    fetchResource(name, stateMode, ac.signal).then(
      (response) => { if (!ac.signal.aborted) setState({ status: 'ready', response }); },
      (error: Error) => { if (!ac.signal.aborted) setState({ status: 'error', error }); }
    );
    return () => ac.abort();
  }, [name, stateMode, attempt]);

  return { state, retry: () => setAttempt((a) => a + 1) };
}
